import TokenInputController from '@/src/components/Commons/Controllers/TokenInputController/TokenInputController';
import { FundWithDetailsEntity, ProtocolEntity, SwapOfferEntity } from '@/src/lib/MayzSmartDB/Entities/index';
import { ARROW_DOWN_UP } from '@/src/lib/SmartDB/Commons/Constants/images';
import { useTokensStore, useWalletStore } from '@/src/store';
import { useMemo } from 'react';
import BtnConfirmation from './Controls/BtnConfirmation';
import Conversion from './ControlsPro/Conversion';
import OfferPart from './ControlsPro/OfferPart';
import styles from './SwapForm.module.scss';

interface SwapFormProps {
    protocol?: ProtocolEntity;
    fund: FundWithDetailsEntity;
    isBuy: boolean;
    isTokensStoreReady: boolean;
    isAddingTokens: boolean;
    isLoadingSwapOffers: boolean;
    swapOffers: SwapOfferEntity[];
    swapOffer: SwapOfferEntity | undefined;
    setSwapOffer: (current?: SwapOfferEntity | undefined) => void;
    isValidInputToGive: boolean;
    setIsValidInputToGive: (isValid: boolean) => void;
    isValidInputToGet: boolean;
    setIsValidInputToGet: (isValid: boolean) => void;
    amount_ADA: bigint;
    amount_FT: bigint;
    amount_AfterCommission_FT: bigint;
    commission_ADA: bigint;
    commission_FT: bigint;
    amount_AfterCommission_ADA: bigint;
    maxAmount_FT_Available: bigint | undefined;
    maxAmount_ADA_Available: bigint | undefined;
    price_Adjusted_For_Fee?: bigint;
    isValidTx: boolean;
    isProcessingTx: boolean;
    setShowProcessingTx: (show: boolean) => void;
    setShowUserConfirmation: (show: boolean) => void;
    available_ADA_in_Wallet: bigint;
    available_forSpend_ADA_in_Wallet: bigint;
    handleChange_toGet: (amount_toGet: bigint, isBuy_?: boolean) => void;
    handleChange_toGive: (amount_toGive: bigint, isBuy_?: boolean) => void;
    handleClickToggleBuySell: () => void;
    show_SwapOffer: (swapOffer: SwapOfferEntity) => string;
}

export default function SwapForm(props: SwapFormProps) {
    //--------------------------------------
    const tokensStore = useTokensStore();
    const walletStore = useWalletStore();
    //--------------------------------------
    const {
        protocol,
        fund,
        isBuy,
        isTokensStoreReady,
        isAddingTokens,
        isLoadingSwapOffers,
        swapOffers,
        swapOffer,
        setSwapOffer,
        isValidInputToGive,
        setIsValidInputToGive,
        isValidInputToGet,
        setIsValidInputToGet,

        amount_ADA,
        amount_FT,
        amount_AfterCommission_FT,
        commission_ADA,
        commission_FT,

        maxAmount_FT_Available,
        maxAmount_ADA_Available,

        amount_AfterCommission_ADA,
        price_Adjusted_For_Fee,

        isValidTx,
        isProcessingTx,
        setShowProcessingTx,
        setShowUserConfirmation,

        available_ADA_in_Wallet,
        available_forSpend_ADA_in_Wallet,

        handleChange_toGet,
        handleChange_toGive,
        handleClickToggleBuySell,

        show_SwapOffer,
    } = props;
    //--------------------------------------
    const { metadataGive, imageGive, decimalsGive, tickerGive, metadataGet, imageGet, decimalsGet, tickerGet } = useMemo(() => {
        const metadataGive = isBuy === true ? tokensStore.getTokenPriceAndMetadata('', '') : fund.tokenFT;
        const metadataGet = isBuy !== true ? tokensStore.getTokenPriceAndMetadata('', '') : fund.tokenFT;
        return {
            metadataGive,
            imageGive: metadataGive?.image,
            decimalsGive: metadataGive?.decimals,
            tickerGive: metadataGive?.ticker,

            metadataGet,
            imageGet: metadataGet?.image,
            decimalsGet: metadataGet?.decimals,
            tickerGet: metadataGet?.ticker,
        };
    }, [isBuy, fund, tokensStore]);
    //--------------------------------------
    return (
        <div className={styles.formController}>
            <div className={styles.inputController}>
                <TokenInputController
                    CS={isBuy === true ? '' : `${fund.getNET_id_CS()}`}
                    TN_Hex={isBuy === true ? '' : `${fund.fdFundFT_TN_Hex}`}
                    ticker={tickerGive}
                    isTokensStoreReady={isTokensStoreReady}
                    image={imageGive}
                    value={isBuy === true ? amount_ADA : amount_FT}
                    setValue={handleChange_toGive}
                    min={0n}
                    max={isBuy === true ? available_ADA_in_Wallet : walletStore.getTotalOfUnit(fund.getNET_id_CS() + fund.fdFundFT_TN_Hex)}
                    max_available_forSpend={isBuy === true ? available_forSpend_ADA_in_Wallet : walletStore.getTotalOfUnit(fund.getNET_id_CS() + fund.fdFundFT_TN_Hex)}
                    decimals={decimalsGive}
                    isValidInput={isValidInputToGive}
                    setIsValidInput={setIsValidInputToGive}
                />
                <div className={styles.inputControllerSwitch}>
                    <div className={styles.switch} onClick={() => handleClickToggleBuySell()}>
                        <svg width="16" height="16" id="icon" className={`${styles.iconSwicht} ${isBuy !== true ? styles.isSell : ''}`}>
                            <use href={ARROW_DOWN_UP}></use>
                        </svg>
                    </div>
                </div>
                <TokenInputController
                    CS={isBuy !== true ? '' : `${fund.getNET_id_CS()}`}
                    TN_Hex={isBuy !== true ? '' : `${fund.fdFundFT_TN_Hex}`}
                    ticker={tickerGet}
                    isTokensStoreReady={isTokensStoreReady}
                    image={imageGet}
                    value={isBuy === true ? amount_AfterCommission_FT : amount_AfterCommission_ADA}
                    setValue={handleChange_toGet}
                    min={0n}
                    // max={swapOffer !== undefined ? (isBuy === true ? swapOffer.sodAmount_FT_Available : swapOffer.sodAmount_ADA_Available) : 0n}
                    max={swapOffer !== undefined ? (isBuy === true ? maxAmount_FT_Available ?? 0n : maxAmount_ADA_Available ?? 0n) : 0n}
                    decimals={decimalsGet}
                    isValidInput={isValidInputToGet}
                    setIsValidInput={setIsValidInputToGet}
                />
            </div>
            {walletStore.swProInterface === true && (
                <div className={styles.swapProSection}>
                    <OfferPart
                        isBuy={isBuy}
                        isLoadingSwapOffers={isLoadingSwapOffers}
                        show_SwapOffer={show_SwapOffer}
                        swapOffers={swapOffers}
                        swapOffer={swapOffer}
                        setSwapOffer={setSwapOffer}
                    />
                    <Conversion
                        isBuy={isBuy}
                        fund={fund}
                        swapOffer={swapOffer}
                        showRefresh={true}
                        showValid={true}
                        commission={isBuy ? commission_FT : commission_ADA}
                        price_Adjusted_For_Fee={price_Adjusted_For_Fee}
                    />
                </div>
            )}
            <BtnConfirmation
                isBuy={isBuy}
                fund={fund}
                isTokensStoreReady={isTokensStoreReady}
                isAddingTokens={isAddingTokens}
                isLoadingSwapOffers={isLoadingSwapOffers}
                swapOffer={swapOffer}
                swapOffers={swapOffers}
                amount_toGive={isBuy === true ? amount_ADA : amount_FT}
                commission={isBuy === true ? commission_FT : commission_ADA}
                amount_toGet={isBuy === true ? amount_AfterCommission_FT : amount_AfterCommission_ADA}
                isValidTx={isValidTx}
                isProcessingTx={isProcessingTx}
                setShowProcessingTx={setShowProcessingTx}
                setShowUserConfirmation={setShowUserConfirmation}
            />
        </div>
    );
}
